import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { onboardingApi, type OnboardingState } from "@/lib/api/onboarding";
import { useAuthStore } from "@/stores/auth-store";

export const onboardingKeys = {
  all: ["onboarding"] as const,
};

/** What the signed-in user must do before using the app. */
export function useOnboardingState() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  return useQuery({
    queryKey: onboardingKeys.all,
    queryFn: () => onboardingApi.state(),
    select: (response): OnboardingState => response.data,
    enabled: isAuthenticated,
  });
}

/**
 * Onboarding mutations. Each refetches the onboarding state; creating or
 * joining an organization also re-reads the session, since the user's
 * organization changed.
 */
export function useOnboardingActions() {
  const queryClient = useQueryClient();
  const checkAuth = useAuthStore((s) => s.checkAuth);
  const refresh = async () => {
    await checkAuth();
    await queryClient.invalidateQueries();
  };
  return {
    requestToJoin: useMutation({ mutationFn: () => onboardingApi.requestToJoin(), onSuccess: refresh }),
    cancelRequest: useMutation({ mutationFn: () => onboardingApi.cancelRequest(), onSuccess: refresh }),
    selectPlan: useMutation({
      mutationFn: (plan: "free" | "team") => onboardingApi.selectPlan(plan),
      onSuccess: refresh,
    }),
    joinManagedAiWaitlist: useMutation({
      mutationFn: () => onboardingApi.joinManagedAiWaitlist(),
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: ["billing"] });
      },
    }),
    requestTeamAccess: useMutation({ mutationFn: () => onboardingApi.requestTeamAccess() }),
    completeAiSetup: useMutation({ mutationFn: () => onboardingApi.completeAiSetup(), onSuccess: refresh }),
    completeGitHubSetup: useMutation({ mutationFn: () => onboardingApi.completeGitHubSetup(), onSuccess: refresh }),
    createOrganization: useMutation({
      mutationFn: (name: string) => onboardingApi.createOrganization(name),
      onSuccess: refresh,
    }),
  };
}
